import { useState } from 'react';
import { Server, Monitor, Wifi, HardDrive, Printer, Shield, Router, Network, Box, Plus, Key, Cctv, X } from 'lucide-react';
import AddAssetModal from '../Assets/AddAssetModal';
import AddCredentialModal from '../Assets/AddCredentialModal'; // Import the credential modal
import { hasPermission } from '../../utils/permissionsHelper';

interface Asset {
    id: number;
    name: string;
    asset_type: string;
    hostname: string;
    ip_address: string;
    mac_address: string;
    client_id: number;
    status: string;
    total_ports?: number | null;
}

interface AssetsPaletteProps {
    assets: Asset[];
    loading: boolean;
    onAssetAdded?: () => void;
    usedAssetIds?: Set<number>; // New Prop for disabling used assets
    callTopology: () => void;
    onClose?: () => void;
}

const getAssetIcon = (type: string) => {
    const icons: Record<string, any> = {
        camera: Cctv,
        server: Server,
        workstation: Monitor,
        access_point: Wifi,
        nvr: HardDrive,
        printer: Printer,
        firewall: Shield,
        router: Router,
        switch: Router,
        storage: HardDrive,
        local_asset: Server,
        isp: Wifi,
        other: Box,
    };
    return icons[type] || Box;
};

export default function AssetsPalette({ assets = [], loading, onAssetAdded, usedAssetIds = new Set(), callTopology, onClose }: AssetsPaletteProps) {
    const [showAddModal, setShowAddModal] = useState(false);

    // Credential Modal State
    const [credentialModalOpen, setCredentialModalOpen] = useState(false);
    const [selectedAssetId, setSelectedAssetId] = useState<number | null>(null);
    const [selectedAssetType, setSelectedAssetType] = useState<string>('');
    const [credentialClientId, setCredentialClientId] = useState<any>(null);
    const canCreate = hasPermission('assets', 'create')

    const onDragStart = (event: React.DragEvent, asset: Asset) => {
        // Prevent dragging if asset is already on canvas
        if (usedAssetIds.has(asset.id)) {
            event.preventDefault();
            return;
        }

        const nodeType = asset.asset_type === 'switch' ? 'switchNode' : 'assetNode';
        event.dataTransfer.setData('application/reactflow', nodeType);
        event.dataTransfer.setData('assetType', asset.asset_type);
        event.dataTransfer.setData('assetName', asset.name);
        event.dataTransfer.setData('assetId', `asset-${asset.id}`);
        event.dataTransfer.setData('assetIp', asset.ip_address);
        event.dataTransfer.setData('totalPorts', (asset.total_ports || 24).toString());
        event.dataTransfer.setData('agentId', String((asset as any).agent_id || ''));
        event.dataTransfer.setData('organizationId', String((asset as any).organization_id || ''));
        event.dataTransfer.effectAllowed = 'move';
    };

    const handleCredentialClick = (e: React.MouseEvent, assetId: number, clientId: number, assetType: string) => {
        e.stopPropagation(); // Prevent drag start when clicking the button
        e.preventDefault();
        setSelectedAssetId(assetId);
        setCredentialClientId(clientId);
        setSelectedAssetType(assetType || '');
        setCredentialModalOpen(true);
    };

    const safeAssets = Array.isArray(assets) ? assets : [];
    const groupedAssets = safeAssets.reduce((acc, asset) => {
        const type = asset.asset_type || 'other';
        if (!acc[type]) acc[type] = [];
        acc[type].push(asset);
        return acc;
    }, {} as Record<string, Asset[]>);

    return (
        <>
            <div className="w-64 bg-white border-r border-slate-200 flex flex-col h-full select-none">
                {/* Header */}
                <div className="px-3 py-2 border-b border-slate-200 flex items-center justify-between bg-white/80 backdrop-blur-sm sticky top-0 z-10">
                    <div className="flex items-center gap-2">
                        <Network className="w-4 h-4 text-[#422568]" />
                        <span className="text-xs font-bold text-slate-800 uppercase tracking-wide">Assets</span>
                    </div>
                    <div className="flex items-center gap-2">
                        {!loading && (
                            <span className="text-[10px] bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded-full border border-slate-200">
                                {safeAssets.length}
                            </span>
                        )}
                        {onClose && (
                            <button
                                onClick={onClose}
                                className="p-1 hover:bg-slate-100 rounded text-slate-400 hover:text-rose-500 transition-colors"
                                title="Hide Assets"
                            >
                                <X className="w-3.5 h-3.5" />
                            </button>
                        )}
                    </div>
                </div>

                {/* Create Asset Button */}
                {canCreate && (
                <div className="p-2 border-b border-slate-200 bg-slate-50">
                    <button
                        onClick={() => setShowAddModal(true)}
                        className="w-full flex items-center justify-center gap-2 py-1.5 bg-white hover:bg-[#422568] text-[#422568] hover:text-[#422568] text-xs font-semibold rounded-md border border-slate-200 hover:border-[#422568] transition-all duration-200 shadow-sm"
                    >
                        <Plus className="w-3.5 h-3.5" />
                        Create Asset
                    </button>
                </div>
                )}

                {/* Content */}
                <div className="flex-1 overflow-y-auto custom-scroll p-2 space-y-3 bg-slate-50/50">
                    {loading ? (
                        <div className="flex justify-center py-4">
                            <div className="w-5 h-5 border-2 border-[#422568] border-t-transparent rounded-full animate-spin" />
                        </div>
                    ) : safeAssets.length === 0 ? (
                        <div className="text-center py-8 opacity-50">
                            <p className="text-[10px] text-slate-500 font-medium">No assets found</p>
                        </div>
                    ) : (
                        Object.entries(groupedAssets).map(([type, typeAssets]) => {
                            const Icon = getAssetIcon(type);
                            return (
                                <div key={type} className="space-y-1">
                                    <div className="flex items-center justify-between px-1 text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">
                                        <div className="flex items-center gap-1.5">
                                            <Icon className="w-3 h-3" />
                                            {type.replace(/_/g, ' ')}
                                        </div>
                                        <span className="bg-slate-200 text-slate-600 px-1.5 rounded-sm">{typeAssets.length}</span>
                                    </div>

                                    <div className="grid gap-1">
                                        {typeAssets.map((asset) => {
                                            const AssetIcon = getAssetIcon(asset.asset_type);
                                            const isUsed = usedAssetIds.has(asset.id);

                                            return (
                                                <div
                                                    key={asset.id}
                                                    draggable={!isUsed}
                                                    onDragStart={(e) => onDragStart(e, asset)}
                                                    className={`group flex items-center gap-2 p-1.5 border rounded relative overflow-hidden pr-8 transition-all
                                                        ${isUsed
                                                            ? 'bg-slate-50 border-slate-200 opacity-60 cursor-not-allowed grayscale'
                                                            : 'bg-white hover:bg-slate-50 border-slate-200 hover:border-slate-300 hover:shadow-sm cursor-grab active:cursor-grabbing'
                                                        }`}
                                                >
                                                    {/* Status Dot */}
                                                    <div
                                                        className={`w-1.5 h-1.5 rounded-full ml-1 flex-shrink-0 ${asset.status === 'active' ? 'bg-green-500 shadow-[0_0_4px_rgba(34,197,94,0.4)]' :
                                                            asset.status === 'inactive' ? 'bg-slate-400' :
                                                                asset.status === 'maintenance' ? 'bg-yellow-500' : 'bg-red-500'
                                                            }`}
                                                    />

                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center justify-between gap-2">
                                                            <span className={`text-[11px] font-semibold truncate ${isUsed ? 'text-slate-400' : 'text-slate-800'}`}>
                                                                {asset.name}
                                                            </span>
                                                            <AssetIcon className={`w-3 h-3 ${isUsed ? 'text-slate-300' : 'text-slate-400 group-hover:text-blue-500'}`} />
                                                        </div>

                                                        <div className="flex items-center justify-between mt-0.5">
                                                            <span className={`text-[9px] font-mono truncate ${isUsed ? 'text-slate-400' : 'text-slate-500'}`}>
                                                                {asset.ip_address}
                                                            </span>
                                                            {asset.asset_type === 'switch' && (
                                                                <span className={`text-[9px] ${isUsed ? 'text-slate-400' : 'text-slate-500'}`}>
                                                                    {asset.total_ports || 24}P
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>

                                                    {/* Used Badge or Key Button */}
                                                    {isUsed ? (
                                                        <div className="absolute right-2 top-1/2 -translate-y-1/2 text-[8px] font-bold text-slate-500 bg-slate-100 border border-slate-200 px-1 rounded">
                                                            USED
                                                        </div>
                                                    ) : (
                                                        <button
                                                            onClick={(e) => handleCredentialClick(e, asset.id, asset.client_id, asset.asset_type)}
                                                            className="absolute right-1 top-1/2 -translate-y-1/2 p-1.5 text-slate-400 hover:text-[#422568] hover:bg-[#422568] rounded-md transition-colors opacity-0 group-hover:opacity-100"
                                                            title="Manage Credentials"
                                                        >
                                                            <Key className="w-3.5 h-3.5" />
                                                        </button>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
            </div>

            {/* Add Asset Modal */}
            <AddAssetModal
                open={showAddModal}
                onClose={() => setShowAddModal(false)}
                onSuccess={() => {
                    if (onAssetAdded) onAssetAdded();
                }}
            />

            {/* Credential Modal */}
            <AddCredentialModal
                open={credentialModalOpen}
                assetId={selectedAssetId}
                clientId={credentialClientId}
                assetType={selectedAssetType}
                onClose={() => {
                    setCredentialModalOpen(false);
                    setSelectedAssetId(null);
                    setSelectedAssetType('');
                    callTopology();
                    if (onAssetAdded) onAssetAdded();
                }} />
        </>
    );
}